import classnames from 'classnames';
import locale from '@glu/locale';
import { Button, TERTIARY, SMALL } from '@glu/buttons-react';
import useStyles from './PageSelectorStartToEnd.styles';
import { IPageSelectorProps } from '../PageSelector/PageSelector';
import { IPaginationProps } from '../Pagination/Pagination';

export const displayCount = 4;

interface IPageSelectorStartEndProps {
  last: IPageSelectorProps['last'];
  /** the number of pages to display */
  numberOfPages: number;
  onGoToPage: IPaginationProps['onPageChange'];
  page: IPaginationProps['page'];
  /** An array of pages to display */
  pages: number[];
}

export default function PageSelectorStartEnd({
  last,
  numberOfPages,
  onGoToPage,
  page,
  pages,
}: IPageSelectorStartEndProps) {
  const classes = useStyles();
  const startEllipsis = page >= displayCount - 1 && numberOfPages >= displayCount + 1;
  const endEllipsis = last - page > 2 && numberOfPages > displayCount + 1;
  const startRange = startEllipsis
    ? last === page
      ? last - 2 // Start 2 from the last item if on last page
      : page - 1 // Start one before the current page if not last page
    : 1; // Start on first index if at beginning of range without ellipsis
  const endRange = endEllipsis
    ? page === 0
      ? 3 // For first item display 3 records if display end ellipsis
      : page + 2 // Display 3 records including current page
    : last; // Display last index if no ending ellipsis

  return (
    <>
      <Button
        aria-current={page === 0}
        aria-label={locale.get('dataComponents.viewPageOf', '1', `${numberOfPages}`)}
        className={classnames({ [classes.activePage]: page === 0 })}
        data-qa="pagination-first-page"
        onClick={() => onGoToPage(0)}
        size={SMALL}
        variant={TERTIARY}
      >
        1
      </Button>
      {startEllipsis && <span className={classes.pageEllipsis}>&hellip;</span>}
      {pages.slice(startRange, endRange).map((pageDisplay) => (
        <Button
          key={pageDisplay}
          aria-current={page === pageDisplay}
          aria-label={locale.get(
            'dataComponents.viewPageOf',
            `${pageDisplay + 1}`,
            `${numberOfPages}`
          )}
          className={classnames({ [classes.activePage]: pageDisplay === page })}
          data-qa={`pagination-page-${pageDisplay}`}
          onClick={() => onGoToPage(pageDisplay)}
          size={SMALL}
          variant={TERTIARY}
        >
          {pageDisplay + 1}
        </Button>
      ))}
      {endEllipsis && <span className={classes.pageEllipsis}>&hellip;</span>}
      {pages.length > 1 && (
        <Button
          aria-current={page + 1 === pages.length}
          aria-label={locale.get(
            'dataComponents.viewPageOf',
            `${pages.length}`,
            `${numberOfPages}`
          )}
          className={classnames({ [classes.activePage]: page === pages.length - 1 })}
          data-qa="pagination-last-page"
          onClick={() => onGoToPage(pages.length - 1)}
          size={SMALL}
          variant={TERTIARY}
        >
          {pages.length}
        </Button>
      )}
    </>
  );
}
